import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Send, UserCheck } from "lucide-react";

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export const OwnerApprovalLine = ({ r }) => {
  const oa = r.owner_approval;
  if (!oa) return null;
  const names = (oa.sent_to || []).map((o) => o.name).join(", ");
  if (oa.status === "awaiting" && r.status === "pending") {
    return (
      <p className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-violet-500/10 px-2 py-1 text-xs font-medium text-violet-700 dark:text-violet-300" data-testid={`request-owner-awaiting-${r.id}`}>
        <UserCheck className="h-3.5 w-3.5" /> Awaiting owner approval · sent to {names} by {oa.sent_by} · {fmtAt(oa.sent_at)}
      </p>
    );
  }
  if (oa.decided_by) {
    return (
      <p className="mt-2 text-xs font-medium text-muted-foreground" data-testid={`request-owner-decided-${r.id}`}>
        {oa.status === "approved" ? "Approved" : "Declined"} by owner {oa.decided_by} on {fmtAt(oa.decided_at)}
      </p>
    );
  }
  return null;
};

export const SendToOwnersDialog = ({ request, onClose, onSent }) => {
  const [owners, setOwners] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!request) return;
    setOwners(null);
    const already = new Set((request.owner_approval?.sent_to || []).map((o) => o.id));
    api.get(`/requests/${request.id}/owners`).then((r) => {
      setOwners(r.data);
      setSel(new Set(r.data.length === 1 ? [r.data[0].id] : r.data.filter((o) => already.has(o.id)).map((o) => o.id)));
    }).catch(() => setOwners([]));
  }, [request]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/requests/${request.id}/send-to-owners`, { owner_ids: [...sel] });
      if (!r.data.mail_configured) toast.warning("Marked as awaiting owner approval, but email isn't set up — no email sent");
      else if (r.data.errors?.length) toast.error(`Some emails failed: ${r.data.errors.join("; ")}`);
      else toast.success(`Approval email sent to ${r.data.sent.join(", ")}`);
      onSent();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="send-owners-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Send to Owner for Approval</DialogTitle>
          <DialogDescription>{request?.title} · {request?.property_name}. Each owner you pick gets an email with a link to log in and Approve or Decline. One decision is enough.</DialogDescription>
        </DialogHeader>
        {owners === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /> : owners.length === 0 ? (
          <p className="rounded-sm border border-dashed border-border p-4 text-center text-sm text-muted-foreground" data-testid="send-owners-none">No owner accounts for this property — ask Admin to add one.</p>
        ) : (
          <div className="space-y-2">
            {owners.map((o) => (
              <label key={o.id} className="flex cursor-pointer items-center gap-3 rounded-sm border border-border px-3 py-2 hover:bg-muted/40" data-testid={`send-owner-${o.id}`}>
                <Checkbox checked={sel.has(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`send-owner-check-${o.id}`} />
                <span className="text-sm"><span className="font-medium">{o.name}</span><span className="block text-xs text-muted-foreground">{o.email}</span></span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy || sel.size === 0} data-testid="send-owners-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send{sel.size ? ` (${sel.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
